import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, ClipboardCheck, HeartPulse, FlaskConical, ExternalLink, Check } from "lucide-react";
import { useLang } from "../i18n";
import { api, API } from "../lib/api";
import { ASSETS, TRACKMYHORSE_URL } from "../data/safety";
import { toast } from "sonner";

const pillarIcons = [ClipboardCheck, HeartPulse, FlaskConical];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { t, lang } = useLang();
  const [horses, setHorses] = useState([]);
  const [heroVideo, setHeroVideo] = useState(null);
  const [email, setEmail] = useState("");
  const [subbed, setSubbed] = useState(false);

  useEffect(() => {
    api.get("/horses").then((r) => setHorses(r.data)).catch(() => {});
    api.get("/videos").then((r) => { if (r.data?.length) setHeroVideo(r.data[0]); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email, lang });
      setSubbed(true);
      setEmail("");
      toast.success(t.home.newsletterOk);
    } catch {
      toast.error("Fehler / Error");
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video src={`${API}/videos/${heroVideo.id}/file`} autoPlay muted loop playsInline
              poster={ASSETS.hero} className="w-full h-full object-cover object-center opacity-60" />
          ) : (
            <img src={ASSETS.hero} alt="" className="w-full h-full object-cover object-center opacity-60" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-nacht via-nacht/85 to-nacht/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-nacht via-transparent to-transparent" />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 pt-20 pb-24 md:pt-32 md:pb-36">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 instrument-label text-bronze-soft border border-bronze/30 rounded-full px-3 py-1.5">
              <ShieldCheck size={14} /> {t.hero.kicker}
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight whitespace-pre-line">
              {t.hero.title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-wachs-dim max-w-xl leading-relaxed">{t.hero.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/safety"
                data-testid="hero-cta-safety"
                className="group inline-flex items-center gap-2 bg-bronze text-nacht font-medium px-6 py-3 rounded-md hover:bg-bronze-soft transition-colors"
              >
                {t.hero.ctaSafety}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-story"
                className="inline-flex items-center gap-2 border border-wachs/25 text-wachs px-6 py-3 rounded-md hover:border-bronze/60 hover:text-bronze-soft transition-colors"
              >
                {t.hero.ctaStory}
              </Link>
            </div>
            <p className="mt-6 instrument-label text-wachs-dim/70 flex items-center gap-2">
              <FlaskConical size={13} className="text-bronze" /> {t.hero.badge}
            </p>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-24">
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{t.home.pillarsTitle}</h2>
          <p className="mt-4 text-wachs-dim leading-relaxed">{t.home.pillarsSub}</p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {t.home.pillars.map((p, i) => {
            const Icon = pillarIcons[i];
            return (
              <motion.div
                key={p.t}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                variants={fadeUp}
                className="grid-panel rounded-lg p-6 hover:border-bronze/40 transition-colors"
              >
                <div className="h-11 w-11 rounded-md bg-bronze/12 border border-bronze/25 flex items-center justify-center text-bronze-soft">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-serif text-2xl">{p.t}</h3>
                <p className="mt-2 text-sm text-wachs-dim leading-relaxed">{p.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HORSES */}
      <section className="mx-auto max-w-6xl px-4 py-8 md:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{t.home.horsesTitle}</h2>
            <p className="mt-3 text-wachs-dim">{t.home.horsesSub}</p>
          </div>
        </div>
        <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-3">
          {horses.map((h, i) => (
            <motion.article
              key={h.id}
              data-testid={`horse-card-${h.id}`}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              custom={i}
              variants={fadeUp}
              className={`group relative overflow-hidden rounded-lg border border-bronze/15 ${i === 0 ? "col-span-2 lg:col-span-2 lg:row-span-2" : ""}`}
            >
              <div className={`${i === 0 ? "aspect-[16/11] lg:aspect-auto lg:h-full" : "aspect-square"} overflow-hidden`}>
                {h.video ? (
                  <video
                    src={h.video}
                    poster={h.image || undefined}
                    aria-label={h.name}
                    autoPlay muted loop playsInline
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <img src={h.image} alt={h.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-nacht via-nacht/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <p className="instrument-label text-bronze-soft flex items-center gap-2 flex-wrap">
                  <span>{lang === "de" ? h.titleDe : h.titleEn}</span>
                  {h.born && <span className="text-wachs-dim/80 normal-case tracking-normal">· {lang === "de" ? "geb." : "b."} {h.born}</span>}
                </p>
                <h3 className="font-serif text-xl md:text-2xl leading-tight">{h.name}</h3>
                {i === 0 && (
                  <p className="mt-2 text-sm text-wachs-dim max-w-md hidden md:block">{lang === "de" ? h.bioDe : h.bioEn}</p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* TRACKMYHORSE LINK-OUT */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid-panel rounded-lg p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl">{t.home.trackTitle}</h3>
            <p className="mt-2 text-sm text-wachs-dim max-w-xl">{t.home.trackSub}</p>
          </div>
          <a
            href={TRACKMYHORSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trackmyhorse-link"
            className="inline-flex items-center gap-2 whitespace-nowrap border border-bronze/40 text-bronze-soft px-5 py-3 rounded-md hover:bg-bronze/10 transition-colors"
          >
            {t.home.trackCta} <ExternalLink size={16} />
          </a>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="mx-auto max-w-6xl px-4 py-16">
        <div className="relative overflow-hidden rounded-xl border border-bronze/20 bg-tiefsee p-8 md:p-12">
          <div className="max-w-xl">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{t.home.newsletterTitle}</h2>
            <p className="mt-3 text-wachs-dim">{t.home.newsletterSub}</p>
            {subbed ? (
              <p className="mt-6 inline-flex items-center gap-2 text-ready" data-testid="newsletter-success">
                <Check size={18} /> {t.home.newsletterOk}
              </p>
            ) : (
              <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.home.newsletterPlaceholder}
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-nacht border border-bronze/25 rounded-md px-4 py-3 text-sm outline-none focus:border-bronze transition-colors"
                />
                <button type="submit" data-testid="newsletter-submit" className="bg-bronze text-nacht font-medium px-6 py-3 rounded-md hover:bg-bronze-soft transition-colors">
                  {t.home.newsletterCta}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
